import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FileText, ArrowLeft, CreditCard, Phone, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { COMPANY_INFO } from '../data/siteData';

import { API } from '../config/api';

export default function InvoiceDetail() {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const [tip, setTip] = useState('');

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  const fetchInvoice = async () => {
    try {
      const res = await axios.get(`${API}/invoices/${id}`);
      setInvoice(res.data);
    } catch {
      setError('Invoice not found.');
    } finally {
      setLoading(false);
    }
  };

  const tipAmount = parseFloat(tip) || 0;
  const processingFee = invoice ? round2(invoice.total * 0.03) : 0;
  const totalWithFeeAndTip = invoice ? round2(invoice.total + processingFee + tipAmount) : 0;

  function round2(n) { return Math.round(n * 100) / 100; }

  const handlePay = async () => {
    setPaying(true);
    try {
      const res = await axios.post(`${API}/invoices/${id}/checkout`, {
        origin_url: window.location.origin,
        tip: tipAmount,
      });
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Payment failed. Please try again.');
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-slate-400">Loading invoice...</p>
      </div>
    );
  }

  if (error && !invoice) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="invoice-not-found">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-bold text-slate-900 mb-3">{error}</h2>
          <Link to="/pay" className="text-sky-700 font-medium hover:underline">Back to Search</Link>
        </div>
      </div>
    );
  }

  const isPaid = invoice.status === 'paid';

  return (
    <div data-testid="invoice-detail-page" className="py-8 md:py-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/pay"
          data-testid="invoice-back-link"
          className="flex items-center gap-2 text-slate-400 text-sm mb-6 hover:text-sky-700 transition-colors w-fit"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Search
        </Link>

        {/* Invoice card */}
        <div className="bg-white rounded-2xl border border-sky-100 shadow-lg overflow-hidden" data-testid="invoice-card">
          {/* Header */}
          <div className="bg-sky-700 text-white p-6 sm:p-8">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sky-200 text-xs font-bold uppercase tracking-widest mb-1">Invoice</p>
                <h1 className="font-heading text-2xl sm:text-3xl font-bold">{invoice.invoice_number}</h1>
              </div>
              <div className="text-right">
                {isPaid ? (
                  <div className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                    <CheckCircle className="w-4 h-4" /> Paid
                  </div>
                ) : (
                  <div className="bg-amber-400 text-amber-900 px-4 py-2 rounded-full text-sm font-semibold">
                    Unpaid
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-6 sm:p-8">
            {/* Customer info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">Bill To</p>
                <p className="font-semibold text-slate-900">{invoice.customer_name}</p>
                <p className="text-slate-500 text-sm">{invoice.address}</p>
                {invoice.email && <p className="text-slate-500 text-sm">{invoice.email}</p>}
                {invoice.phone && <p className="text-slate-500 text-sm">{invoice.phone}</p>}
              </div>
              <div className="sm:text-right">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">Date</p>
                <p className="text-slate-600 text-sm">
                  {new Date(invoice.created_at).toLocaleDateString('en-US', {
                    year: 'numeric', month: 'long', day: 'numeric',
                  })}
                </p>
                {invoice.paid_at && (
                  <>
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1 mt-3">Paid On</p>
                    <p className="text-green-600 text-sm font-medium">
                      {new Date(invoice.paid_at).toLocaleDateString('en-US', {
                        year: 'numeric', month: 'long', day: 'numeric',
                      })}
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Line items */}
            <div className="border border-sky-100 rounded-xl overflow-hidden mb-6">
              <div className="bg-sky-50 px-4 py-3 flex justify-between text-xs font-bold uppercase tracking-widest text-slate-500">
                <span>Description</span>
                <span>Amount</span>
              </div>
              {invoice.items.map((item, i) => (
                <div key={i} className="px-4 py-3 flex justify-between border-t border-sky-50">
                  <span className="text-slate-700 text-sm">{item.description}</span>
                  <span className="text-slate-900 font-medium text-sm">${item.amount.toFixed(2)}</span>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="space-y-2 mb-8">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Subtotal</span>
                <span className="text-slate-700">${invoice.subtotal.toFixed(2)}</span>
              </div>
              {invoice.tax > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Tax ({invoice.tax_rate}%)</span>
                  <span className="text-slate-700">${invoice.tax.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between pt-3 border-t border-sky-100">
                <span className="font-heading font-bold text-lg text-slate-900">Total</span>
                <span className="font-heading font-bold text-lg text-sky-700">${invoice.total.toFixed(2)}</span>
              </div>
            </div>

            {invoice.notes && (
              <div className="bg-sky-50 rounded-xl px-4 py-3 mb-6">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">Notes</p>
                <p className="text-slate-600 text-sm">{invoice.notes}</p>
              </div>
            )}

            {error && (
              <div className="bg-red-50 text-red-600 text-sm rounded-xl px-4 py-3 mb-4" data-testid="payment-error">
                {error}
              </div>
            )}

            {/* Pay button */}
            {!isPaid && (
              <>
                {/* Tip section */}
                <div className="bg-sky-50 rounded-xl px-4 py-4 mb-4" data-testid="tip-section">
                  <p className="text-slate-600 text-sm mb-3">
                    If you were more than satisfied with the work performed and want to leave something extra for the team that went out, tips are very appreciated. Thank you for your business!
                  </p>
                  <div className="flex items-center gap-3">
                    <span className="text-slate-500 text-sm font-medium">Tip $</span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={tip}
                      onChange={(e) => setTip(e.target.value)}
                      placeholder="0.00"
                      data-testid="tip-input"
                      className="flex-1 h-10 rounded-xl border border-sky-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                    />
                  </div>
                  <div className="mt-3 pt-3 border-t border-sky-200 space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Invoice Total:</span>
                      <span className="text-slate-700">${invoice.total.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Processing Fee (3%):</span>
                      <span className="text-slate-700">${processingFee.toFixed(2)}</span>
                    </div>
                    {tipAmount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Tip:</span>
                        <span className="text-slate-700">${tipAmount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-1 border-t border-sky-200">
                      <span className="font-heading font-bold text-slate-900">Total Charge:</span>
                      <span className="font-heading font-bold text-sky-700">${totalWithFeeAndTip.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-slate-400 text-center mb-3">Credit card payments include a 3% processing fee</p>

                <Button
                  onClick={handlePay}
                  disabled={paying}
                  data-testid="pay-now-btn"
                  className="w-full bg-sky-700 text-white font-semibold rounded-full py-3 hover:bg-sky-600 text-base"
                >
                  {paying ? (
                    'Redirecting to payment...'
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pay ${totalWithFeeAndTip.toFixed(2)} Now
                    </>
                  )}
                </Button>
              </>
            )}

            {isPaid && (
              <div className="text-center py-4">
                <p className="text-green-600 font-medium">This invoice has been paid. Thank you!</p>
              </div>
            )}

            <div className="text-center mt-4">
              <p className="text-slate-400 text-xs">
                Questions? Call <a href={`tel:${COMPANY_INFO.phone}`} className="text-sky-700">{COMPANY_INFO.phone}</a> or email{' '}
                <a href={`mailto:${COMPANY_INFO.email}`} className="text-sky-700">{COMPANY_INFO.email}</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
